<template>
  <div class="landing-container">
    <!-- Full-screen canvas background -->
    <div id="p5-canvas" class="canvas-background"></div>
    
    <!-- Content overlay -->
    <div class="content-overlay d-flex flex-column align-center justify-center">
      <div class="icon mb-6" @click="quest++">
        <v-icon color="white" x-large>mdi-lightning-bolt</v-icon>
      </div>
      <h2 class="main-title">Hydro Ottawa AI</h2>
      <p class="subtitle">Future • Powered</p>

      <v-btn 
        @click="googleAuth()" 
        :loading="authenticating"
        class="login-btn mt-4"
        large
        rounded
      >
        <v-icon left>mdi-login</v-icon>
        Login to continue
      </v-btn>
      
      <div v-if="quest > 3" class="key" @click="cycleSim()">
        <v-btn icon>
          <v-icon :color="colour">{{ simIcons[currentSim] }}</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, signInWithPopup, signInWithRedirect, GoogleAuthProvider } from 'firebase/auth'
import p5 from 'p5'

export default ({
  name: 'Landing',
  data() {
    return {
      authenticating: false,
      colour: 'white',
      year: new Date().getFullYear(),
      p5Instance: null,
      currentSim: 0,
      simIcons: ['mdi-star-outline', 'mdi-key', 'mdi-bird', 'mdi-content-cut', 'mdi-dots-hexagon'],
      quest: 0,
      clothGhosting: false, // Performance option: set to true for onion skinning effect
    }
  },
  mounted() {
    this.initP5()
  },
  beforeDestroy() {
    if (this.p5Instance) {
      this.p5Instance.remove()
    }
  },
  methods: {
    cycleSim() {
      this.currentSim = (this.currentSim + 1) % 5
      if (this.p5Instance) {
        this.p5Instance.remove()
      }
      this.initP5()
    },
    initP5() {
      const sketch = (p) => {
        let particles = []
        let time = 0
        let gridLines = []

        p.setup = () => {
          const canvas = p.createCanvas(p.windowWidth, p.windowHeight)
          canvas.parent('p5-canvas')
          
          this.setupSimulation(p, particles, gridLines)
        }

        p.draw = () => {
          time += 0.02
          this.drawSimulation(p, particles, gridLines, time)
        }

        p.windowResized = () => {
          p.resizeCanvas(p.windowWidth, p.windowHeight)
        }

        p.mouseMoved = () => {
          this.handleMouseInteraction(p, particles)
        }
      }

      this.p5Instance = new p5(sketch)
    },
    
    setupSimulation(p, particles, gridLines) {
      particles.length = 0
      gridLines.length = 0
      
      switch(this.currentSim) {
        case 0: // Star Field
          // Create stars for the rotating star field
          for (let i = 0; i < 150; i++) {
            let angle = p.random(p.TWO_PI)
            let radius = p.random(50, p.width)
            particles.push({
              angle: angle,
              radius: radius,
              x: p.width/2 + p.cos(angle) * radius,
              y: p.height/2 + p.sin(angle) * radius,
              size: p.random(1, 4),
              brightness: p.random(0.3, 1),
              flickerSpeed: p.random(0.01, 0.05),
              flickerOffset: p.random(p.TWO_PI),
              type: 'star'
            })
          }
          
          // Create shooting stars array (empty initially)
          gridLines.push({
            shootingStars: [],
            lastShootingStar: 0,
            type: 'shootingStarManager'
          })
          break
          
        case 1: // Electrical Particles
          for (let i = 0; i < 80; i++) {
            particles.push({
              x: p.random(p.width),
              y: p.random(p.height),
              vx: p.random(-1, 1),
              vy: p.random(-1, 1),
              energy: p.random(0.5, 1),
              type: 'electrical'
            })
          }
          break
          
        case 2: // Flocking Boids
          for (let i = 0; i < 120; i++) {
            particles.push({
              x: p.random(p.width),
              y: p.random(p.height),
              vx: p.random(-2, 2),
              vy: p.random(-2, 2),
              maxSpeed: 2,
              maxForce: 0.03,
              type: 'boid',
              hue: p.random(180, 240), // Blue-cyan range
              neighbors: []
            })
          }
          break
          
        case 3: // Cloth Fabric
          // Create a full-width cloth grid with self-healing
          let spacing = 12
          let clothWidth = Math.floor(p.width / spacing / 2) + 1 // Ensure full coverage
          let clothHeight = Math.floor(p.height / spacing / 2) // Half screen height
          let startX = Math.floor(p.width / 2) - clothWidth * spacing / 2
          let startY = 40
          
          // Create cloth particles
          for (let x = 0; x < clothWidth; x++) {
            particles[x] = []
            for (let y = 0; y < clothHeight; y++) {
              particles[x][y] = {
                x: startX + x * spacing,
                y: startY + y * spacing,
                oldX: startX + x * spacing,
                oldY: startY + y * spacing,
                pinned: y === 0, // Pin entire top row
                connections: {
                  right: x < clothWidth - 1 ? { active: true, cutTime: 0, strength: 1.0 } : null,
                  down: y < clothHeight - 1 ? { active: true, cutTime: 0, strength: 1.0 } : null,
                  diagonal1: (x < clothWidth - 1 && y < clothHeight - 1) ? { active: true, cutTime: 0, strength: 1.0 } : null,
                  diagonal2: (x > 0 && y < clothHeight - 1) ? { active: true, cutTime: 0, strength: 1.0 } : null
                },
                type: 'clothParticle'
              }
            }
          }
          break
          
        case 4: // Particle Swarm
          for (let i = 0; i < 200; i++) {
            particles.push({
              x: p.random(p.width),
              y: p.random(p.height),
              vx: p.random(-2, 2),
              vy: p.random(-2, 2),
              targetX: p.width/2,
              targetY: p.height/2,
              hue: p.random(0, 360),
              size: p.random(2, 6),
              type: 'swarmParticle'
            })
          }
          break
          

      }
    },
    
    drawSimulation(p, particles, gridLines, time) {
      switch(this.currentSim) {
        case 0: // Star Field
          p.background(5, 5, 15, 255) // Dark space background
          this.drawStarFieldSim(p, particles, gridLines, time)
          break
          
        case 1: // Electrical Particles
          p.background(8, 12, 20, 25)
          this.drawElectricalSim(p, particles, time)
          break
          
        case 2: // Flocking Boids
          p.background(5, 10, 25, 40)
          this.drawFlockingSim(p, particles, time)
          break
          
        case 3: // Cloth Fabric
          let bgAlpha = this.clothGhosting ? 40 : 255
          p.background(15, 15, 25, bgAlpha)
          this.drawClothSim(p, particles, time)
          break
          
        case 4: // Particle Swarm
          p.background(5, 5, 15, 40)
          this.drawParticleSwarmSim(p, particles, time)
          break
          

      }
    },
    
    drawElectricalSim(p, particles, time) {
      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        let particle = particles[i]
        
        // Add some flow field movement
        let angle = p.noise(particle.x * 0.01, particle.y * 0.01, time) * p.TWO_PI * 2
        particle.vx += p.cos(angle) * 0.1
        particle.vy += p.sin(angle) * 0.1
        
        // Apply velocity with damping
        particle.x += particle.vx
        particle.y += particle.vy
        particle.vx *= 0.98
        particle.vy *= 0.98
        
        // Wrap around edges
        if (particle.x < 0) particle.x = p.width
        if (particle.x > p.width) particle.x = 0
        if (particle.y < 0) particle.y = p.height
        if (particle.y > p.height) particle.y = 0
        
        // Draw particle
        p.fill(100 + particle.energy * 155, 150 + particle.energy * 105, 255, 180)
        p.noStroke()
        p.circle(particle.x, particle.y, 2 + particle.energy * 3)
        
        // Draw connections to nearby particles
        for (let j = i + 1; j < particles.length; j++) {
          let other = particles[j]
          let distance = p.dist(particle.x, particle.y, other.x, other.y)
          
          if (distance < 100) {
            let alpha = p.map(distance, 0, 100, 100, 0)
            p.stroke(120, 180, 255, alpha)
            p.strokeWeight(0.5)
            p.line(particle.x, particle.y, other.x, other.y)
          }
        }
      }
      
      // Add some ambient energy waves
      p.noFill()
      for (let i = 0; i < 3; i++) {
        p.stroke(80, 120, 200, 30)
        p.strokeWeight(1)
        let waveY = p.height/2 + p.sin(time + i * 2) * 100
        p.beginShape()
        for (let x = 0; x <= p.width; x += 10) {
          let y = waveY + p.sin(x * 0.01 + time + i) * 20
          p.vertex(x, y)
        }
        p.endShape()
      }
    },
    
    drawFlockingSim(p, particles, time) {
      // Flocking simulation (boids)
      for (let i = 0; i < particles.length; i++) {
        let boid = particles[i]
        
        // Separation
        let separateForce = this.separate(p, boid, particles)
        separateForce = this.mult(separateForce, 1.5)
        
        // Alignment
        let alignForce = this.align(p, boid, particles)
        alignForce = this.mult(alignForce, 1.0)
        
        // Cohesion
        let cohesionForce = this.cohesion(p, boid, particles)
        cohesionForce = this.mult(cohesionForce, 1.0)
        
        // Apply forces
        boid.vx += separateForce.x + alignForce.x + cohesionForce.x
        boid.vy += separateForce.y + alignForce.y + cohesionForce.y
        
        // Limit speed
        let speed = p.sqrt(boid.vx * boid.vx + boid.vy * boid.vy)
        if (speed > boid.maxSpeed) {
          boid.vx = (boid.vx / speed) * boid.maxSpeed
          boid.vy = (boid.vy / speed) * boid.maxSpeed
        }
        
        // Update position
        boid.x += boid.vx
        boid.y += boid.vy
        
        // Wrap around edges
        if (boid.x < 0) boid.x = p.width
        if (boid.x > p.width) boid.x = 0
        if (boid.y < 0) boid.y = p.height
        if (boid.y > p.height) boid.y = 0
        
        // Draw boid with color based on speed
        let colorIntensity = p.map(speed, 0, boid.maxSpeed, 100, 255)
        p.fill(180 + colorIntensity * 0.3, 220 + colorIntensity * 0.2, 255, 200)
        p.noStroke()
        p.circle(boid.x, boid.y, 4 + speed)
        
        // Draw direction indicator
        let angle = p.atan2(boid.vy, boid.vx)
        p.stroke(255, 255, 255, 150)
        p.strokeWeight(1)
        p.line(boid.x, boid.y, 
               boid.x + p.cos(angle) * 8, 
               boid.y + p.sin(angle) * 8)
      }
      
      // Draw connections between nearby boids
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          let distance = p.dist(particles[i].x, particles[i].y, particles[j].x, particles[j].y)
          if (distance < 40) {
            let alpha = p.map(distance, 0, 40, 80, 0)
            p.stroke(150, 200, 255, alpha)
            p.strokeWeight(0.5)
            p.line(particles[i].x, particles[i].y, particles[j].x, particles[j].y)
          }
        }
      }
    },
    
    separate(p, boid, particles) {
      let desiredSeparation = 25
      let sum = { x: 0, y: 0 }
      let count = 0
      
      for (let other of particles) {
        if (other === boid) continue
        let distance = p.dist(boid.x, boid.y, other.x, other.y)
        if (distance > 0 && distance < desiredSeparation) {
          let diff = { x: boid.x - other.x, y: boid.y - other.y }
          diff = this.normalize(diff)
          diff = this.div(diff, distance)
          sum = this.add(sum, diff)
          count++
        }
      }
      
      if (count > 0) {
        sum = this.div(sum, count)
        sum = this.normalize(sum)
        sum = this.mult(sum, boid.maxSpeed)
        sum = this.sub(sum, { x: boid.vx, y: boid.vy })
        sum = this.limit(sum, boid.maxForce)
      }
      return sum
    },
    
    align(p, boid, particles) {
      let neighborDist = 50
      let sum = { x: 0, y: 0 }
      let count = 0
      
      for (let other of particles) {
        if (other === boid) continue
        let distance = p.dist(boid.x, boid.y, other.x, other.y)
        if (distance > 0 && distance < neighborDist) {
          sum = this.add(sum, { x: other.vx, y: other.vy })
          count++
        }
      }
      
      if (count > 0) {
        sum = this.div(sum, count)
        sum = this.normalize(sum)
        sum = this.mult(sum, boid.maxSpeed)
        sum = this.sub(sum, { x: boid.vx, y: boid.vy })
        sum = this.limit(sum, boid.maxForce)
      }
      return sum
    },
    
    cohesion(p, boid, particles) {
      let neighborDist = 50
      let sum = { x: 0, y: 0 }
      let count = 0
      
      for (let other of particles) {
        if (other === boid) continue
        let distance = p.dist(boid.x, boid.y, other.x, other.y)
        if (distance > 0 && distance < neighborDist) {
          sum = this.add(sum, { x: other.x, y: other.y })
          count++
        }
      }
      
      if (count > 0) {
        sum = this.div(sum, count)
        return this.seek(boid, sum)
      }
      return { x: 0, y: 0 }
    },
    
    seek(boid, target) {
      let desired = this.sub(target, { x: boid.x, y: boid.y })
      desired = this.normalize(desired)
      desired = this.mult(desired, boid.maxSpeed)
      
      let steer = this.sub(desired, { x: boid.vx, y: boid.vy })
      steer = this.limit(steer, boid.maxForce)
      return steer
    },
    
    // Vector helper methods
    add(a, b) {
      return { x: a.x + b.x, y: a.y + b.y }
    },
    
    sub(a, b) {
      return { x: a.x - b.x, y: a.y - b.y }
    },
    
    mult(v, scalar) {
      return { x: v.x * scalar, y: v.y * scalar }
    },
    
    div(v, scalar) {
      return { x: v.x / scalar, y: v.y / scalar }
    },
    
    normalize(v) {
      let mag = Math.sqrt(v.x * v.x + v.y * v.y)
      if (mag === 0) return { x: 0, y: 0 }
      return { x: v.x / mag, y: v.y / mag }
    },
    
    limit(v, max) {
      let mag = Math.sqrt(v.x * v.x + v.y * v.y)
      if (mag > max) {
        return { x: (v.x / mag) * max, y: (v.y / mag) * max }
      }
      return v
    },
    
    drawClothSim(p, particles, time) {
      // Living cloth simulation with self-healing
      if (!Array.isArray(particles) || !Array.isArray(particles[0])) return
      
      let clothWidth = particles.length
      let clothHeight = particles[0].length
      let restDistance = 12 // Updated to match new spacing
      let gravity = 0.3
      let damping = 0.995
      let healingTime = 380 // Faster healing (3 seconds at 60fps)
      
      // Self-healing: restore cut connections over time with gradual strength
      let currentFrame = p.frameCount
      for (let x = 0; x < clothWidth; x++) {
        for (let y = 0; y < clothHeight; y++) {
          let particle = particles[x][y]
          
          // Check each connection for healing with gradual strength
          if (particle.connections.right && !particle.connections.right.active) {
            if (currentFrame - particle.connections.right.cutTime > healingTime) {
              particle.connections.right.active = true
              particle.connections.right.healTime = currentFrame // Track when healing started
              particle.connections.right.strength = 0.1 // Start with weak connection
            }
          }
          
          if (particle.connections.down && !particle.connections.down.active) {
            if (currentFrame - particle.connections.down.cutTime > healingTime) {
              particle.connections.down.active = true
              particle.connections.down.healTime = currentFrame
              particle.connections.down.strength = 0.1
            }
          }
          
          if (particle.connections.diagonal1 && !particle.connections.diagonal1.active) {
            if (currentFrame - particle.connections.diagonal1.cutTime > healingTime) {
              particle.connections.diagonal1.active = true
              particle.connections.diagonal1.healTime = currentFrame
              particle.connections.diagonal1.strength = 0.1
            }
          }
          
          if (particle.connections.diagonal2 && !particle.connections.diagonal2.active) {
            if (currentFrame - particle.connections.diagonal2.cutTime > healingTime) {
              particle.connections.diagonal2.active = true
              particle.connections.diagonal2.healTime = currentFrame
              particle.connections.diagonal2.strength = 0.1
            }
          }
        }
      }
      
      // Gradually strengthen healed connections over time
      let strengtheningTime = 120 // 2 seconds to reach full strength
      for (let x = 0; x < clothWidth; x++) {
        for (let y = 0; y < clothHeight; y++) {
          let particle = particles[x][y]
          
          // Strengthen connections that are healing
          if (particle.connections.right && particle.connections.right.active && particle.connections.right.healTime) {
            let timeSinceHeal = currentFrame - particle.connections.right.healTime
            if (timeSinceHeal < strengtheningTime) {
              particle.connections.right.strength = p.map(timeSinceHeal, 0, strengtheningTime, 0.1, 1.0)
            } else {
              particle.connections.right.strength = 1.0
              particle.connections.right.healTime = null // Stop tracking
            }
          }
          
          if (particle.connections.down && particle.connections.down.active && particle.connections.down.healTime) {
            let timeSinceHeal = currentFrame - particle.connections.down.healTime
            if (timeSinceHeal < strengtheningTime) {
              particle.connections.down.strength = p.map(timeSinceHeal, 0, strengtheningTime, 0.1, 1.0)
            } else {
              particle.connections.down.strength = 1.0
              particle.connections.down.healTime = null
            }
          }
          
          if (particle.connections.diagonal1 && particle.connections.diagonal1.active && particle.connections.diagonal1.healTime) {
            let timeSinceHeal = currentFrame - particle.connections.diagonal1.healTime
            if (timeSinceHeal < strengtheningTime) {
              particle.connections.diagonal1.strength = p.map(timeSinceHeal, 0, strengtheningTime, 0.1, 1.0)
            } else {
              particle.connections.diagonal1.strength = 1.0
              particle.connections.diagonal1.healTime = null
            }
          }
          
          if (particle.connections.diagonal2 && particle.connections.diagonal2.active && particle.connections.diagonal2.healTime) {
            let timeSinceHeal = currentFrame - particle.connections.diagonal2.healTime
            if (timeSinceHeal < strengtheningTime) {
              particle.connections.diagonal2.strength = p.map(timeSinceHeal, 0, strengtheningTime, 0.1, 1.0)
            } else {
              particle.connections.diagonal2.strength = 1.0
              particle.connections.diagonal2.healTime = null
            }
          }
        }
      }
      
      // Physics update - Verlet integration
      for (let x = 0; x < clothWidth; x++) {
        for (let y = 0; y < clothHeight; y++) {
          let particle = particles[x][y]
          if (particle.pinned) continue
          
          // Store current position
          let tempX = particle.x
          let tempY = particle.y
          
          // Verlet integration
          particle.x = particle.x * 2 - particle.oldX
          particle.y = particle.y * 2 - particle.oldY + gravity
          
          // Apply damping
          particle.x = particle.oldX + (particle.x - particle.oldX) * damping
          particle.y = particle.oldY + (particle.y - particle.oldY) * damping
          
          // Update old position
          particle.oldX = tempX
          particle.oldY = tempY
          
          // Keep particles on screen
          if (particle.y > p.height + 50) {
            particle.y = p.height + 50
            particle.oldY = particle.y
          }
        }
      }
      
      // Constraint solving - multiple iterations for stability
      for (let iteration = 0; iteration < 3; iteration++) {
        for (let x = 0; x < clothWidth; x++) {
          for (let y = 0; y < clothHeight; y++) {
            let particle = particles[x][y]
            
            // Right connection
            if (particle.connections.right && particle.connections.right.active && x < clothWidth - 1) {
              let neighbor = particles[x + 1][y]
              let strength = particle.connections.right.strength || 1.0
              this.satisfyConstraintWithStrength(particle, neighbor, restDistance, strength)
            }
            
            // Down connection
            if (particle.connections.down && particle.connections.down.active && y < clothHeight - 1) {
              let neighbor = particles[x][y + 1]
              let strength = particle.connections.down.strength || 1.0
              this.satisfyConstraintWithStrength(particle, neighbor, restDistance, strength)
            }
            
            // Diagonal connections for structural integrity
            if (particle.connections.diagonal1 && particle.connections.diagonal1.active && x < clothWidth - 1 && y < clothHeight - 1) {
              let neighbor = particles[x + 1][y + 1]
              let strength = particle.connections.diagonal1.strength || 1.0
              this.satisfyConstraintWithStrength(particle, neighbor, restDistance * 1.414, strength)
            }
            
            if (particle.connections.diagonal2 && particle.connections.diagonal2.active && x > 0 && y < clothHeight - 1) {
              let neighbor = particles[x - 1][y + 1]
              let strength = particle.connections.diagonal2.strength || 1.0
              this.satisfyConstraintWithStrength(particle, neighbor, restDistance * 1.414, strength)
            }
          }
        }
      }
      
      // Draw cloth connections with healing effect
      let healingMaxAlpha = 30 // Much lower maximum alpha for subtlety
      let healingColorShift = 20 // Subtle color shift towards green
      
      for (let x = 0; x < clothWidth; x++) {
        for (let y = 0; y < clothHeight; y++) {
          let particle = particles[x][y]
          
          // Draw right connection
          if (particle.connections.right && x < clothWidth - 1) {
            let neighbor = particles[x + 1][y]
            if (particle.connections.right.active) {
              let strength = particle.connections.right.strength || 1.0
              let alpha = p.map(strength, 0.1, 1.0, 60, 120)
              let weight = p.map(strength, 0.1, 1.0, 0.5, 1.0)
              
              // Show healing with subtle green tint when strength < 1
              if (strength < 1.0) {
                let healingEffect = 1.0 - strength
                let greenShift = healingEffect * 30
                p.stroke(150 - greenShift, 200 + greenShift, 255 - greenShift, alpha)
              } else {
                p.stroke(150, 200, 255, alpha)
              }
              p.strokeWeight(weight)
            } else {
              // Very subtle healing progress indicator
              let healProgress = (currentFrame - particle.connections.right.cutTime) / healingTime
              let alpha = p.map(healProgress, 0, 1, 0, healingMaxAlpha)
              let greenShift = p.map(healProgress, 0, 1, 0, healingColorShift)
              p.stroke(150 - greenShift, 200 + greenShift, 255 - greenShift, alpha)
              p.strokeWeight(0.3 + healProgress * 0.3)
            }
            p.line(particle.x, particle.y, neighbor.x, neighbor.y)
          }
          
          // Draw down connection
          if (particle.connections.down && y < clothHeight - 1) {
            let neighbor = particles[x][y + 1]
            if (particle.connections.down.active) {
              let strength = particle.connections.down.strength || 1.0
              let alpha = p.map(strength, 0.1, 1.0, 60, 120)
              let weight = p.map(strength, 0.1, 1.0, 0.5, 1.0)
              
              // Show healing with subtle green tint when strength < 1
              if (strength < 1.0) {
                let healingEffect = 1.0 - strength
                let greenShift = healingEffect * 30
                p.stroke(150 - greenShift, 200 + greenShift, 255 - greenShift, alpha)
              } else {
                p.stroke(150, 200, 255, alpha)
              }
              p.strokeWeight(weight)
            } else {
              // Very subtle healing progress indicator
              let healProgress = (currentFrame - particle.connections.down.cutTime) / healingTime
              let alpha = p.map(healProgress, 0, 1, 0, healingMaxAlpha)
              let greenShift = p.map(healProgress, 0, 1, 0, healingColorShift)
              p.stroke(150 - greenShift, 200 + greenShift, 255 - greenShift, alpha)
              p.strokeWeight(0.3 + healProgress * 0.3)
            }
            p.line(particle.x, particle.y, neighbor.x, neighbor.y)
          }
        }
      }
      
      // Draw cloth particles (no red dots - all particles look the same)
      p.fill(200, 220, 255, 180)
      p.noStroke()
      for (let x = 0; x < clothWidth; x++) {
        for (let y = 0; y < clothHeight; y++) {
          let particle = particles[x][y]
          p.circle(particle.x, particle.y, 3)
        }
      }
      
      // Draw cutting indicator
      let cutRadius = 25
      p.stroke(255, 100, 100, 150)
      p.strokeWeight(2)
      p.noFill()
      p.circle(p.mouseX, p.mouseY, cutRadius * 2)
      
      // Draw scissors effect
      p.stroke(255, 255, 255, 200)
      p.strokeWeight(1)
      p.line(p.mouseX - 4, p.mouseY - 4, p.mouseX + 4, p.mouseY + 4)
      p.line(p.mouseX - 4, p.mouseY + 4, p.mouseX + 4, p.mouseY - 4)
    },
    
    // Helper function to satisfy distance constraints
    satisfyConstraint(p1, p2, restDistance) {
      let dx = p2.x - p1.x
      let dy = p2.y - p1.y
      let distance = Math.sqrt(dx * dx + dy * dy)
      
      if (distance === 0) return // Avoid division by zero
      
      let difference = restDistance - distance
      let percent = difference / distance / 2
      let offsetX = dx * percent
      let offsetY = dy * percent
      
      if (!p1.pinned) {
        p1.x -= offsetX
        p1.y -= offsetY
      }
      if (!p2.pinned) {
        p2.x += offsetX
        p2.y += offsetY
      }
    },
    
    // Helper function to satisfy distance constraints with variable strength
    satisfyConstraintWithStrength(p1, p2, restDistance, strength) {
      let dx = p2.x - p1.x
      let dy = p2.y - p1.y
      let distance = Math.sqrt(dx * dx + dy * dy)
      
      if (distance === 0) return // Avoid division by zero
      
      let difference = restDistance - distance
      let percent = (difference / distance / 2) * strength // Apply strength multiplier
      let offsetX = dx * percent
      let offsetY = dy * percent
      
      if (!p1.pinned) {
        p1.x -= offsetX
        p1.y -= offsetY
      }
      if (!p2.pinned) {
        p2.x += offsetX
        p2.y += offsetY
      }
    },
    
    drawParticleSwarmSim(p, particles, time) {
      // Enhanced Particle Swarm Simulation
      let centerX = p.width / 2
      let centerY = p.height / 2
      
      for (let i = 0; i < particles.length; i++) {
        let particle = particles[i]
        
        // Swarm behavior: attraction to center with variable strength
        let dx = centerX - particle.x
        let dy = centerY - particle.y
        let centerDistance = p.sqrt(dx * dx + dy * dy)
        
        // Variable attraction based on distance
        if (centerDistance > 50) {
          let attractionStrength = p.map(centerDistance, 50, 300, 0.02, 0.1)
          let angle = p.atan2(dy, dx)
          particle.vx += p.cos(angle) * attractionStrength
          particle.vy += p.sin(angle) * attractionStrength
        }
        
        // Separation from other particles
        for (let j = 0; j < particles.length; j++) {
          if (i === j) continue
          let other = particles[j]
          let distance = p.dist(particle.x, particle.y, other.x, other.y)
          
          if (distance < 30 && distance > 0) {
            let repelStrength = p.map(distance, 0, 30, 0.15, 0)
            let angle = p.atan2(particle.y - other.y, particle.x - other.x)
            particle.vx += p.cos(angle) * repelStrength
            particle.vy += p.sin(angle) * repelStrength
          }
        }
        
        // Add some flow field for organic movement
        let noiseScale = 0.005
        let flowAngle = p.noise(particle.x * noiseScale, particle.y * noiseScale, time * 0.5) * p.TWO_PI * 2
        particle.vx += p.cos(flowAngle) * 0.03
        particle.vy += p.sin(flowAngle) * 0.03
        
        // Limit speed
        let speed = p.sqrt(particle.vx * particle.vx + particle.vy * particle.vy)
        let maxSpeed = 2.5
        if (speed > maxSpeed) {
          particle.vx = (particle.vx / speed) * maxSpeed
          particle.vy = (particle.vy / speed) * maxSpeed
        }
        
        // Apply damping
        particle.vx *= 0.98
        particle.vy *= 0.98
        
        // Update position
        particle.x += particle.vx
        particle.y += particle.vy
        
        // Wrap around edges
        if (particle.x < 0) particle.x = p.width
        if (particle.x > p.width) particle.x = 0
        if (particle.y < 0) particle.y = p.height
        if (particle.y > p.height) particle.y = 0
        
        // Update hue based on speed and position
        particle.hue = (particle.hue + speed * 2) % 360
        
        // Draw particle with glow effect
        let alpha = p.map(speed, 0, maxSpeed, 150, 255)
        
        // Glow effect
        for (let r = particle.size + 4; r > 0; r -= 2) {
          let glowAlpha = p.map(r, 0, particle.size + 4, alpha, 20)
          p.fill(particle.hue, 80, 100, glowAlpha)
          p.noStroke()
          p.circle(particle.x, particle.y, r)
        }
        
        // Main particle
        p.fill(particle.hue, 90, 100, alpha)
        p.noStroke()
        p.circle(particle.x, particle.y, particle.size)
      }
      
      // Draw connections between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          let distance = p.dist(particles[i].x, particles[i].y, particles[j].x, particles[j].y)
          if (distance < 60) {
            let alpha = p.map(distance, 0, 60, 100, 0)
            p.stroke(200, 50, 90, alpha)
            p.strokeWeight(0.5)
            p.line(particles[i].x, particles[i].y, particles[j].x, particles[j].y)
          }
        }
      }
    },
    

    

    
    handleMouseInteraction(p, particles) {
      if (this.currentSim === 0) {
        // Star Field - enhance brightness of nearby stars
        for (let star of particles) {
          let distance = p.dist(p.mouseX, p.mouseY, star.x, star.y)
          if (distance < 100) {
            // Temporarily boost brightness of nearby stars
            let proximityEffect = p.map(distance, 0, 100, 0.5, 0)
            star.brightness = Math.min(star.brightness + proximityEffect, 1.5)
          } else {
            // Gradually return to normal brightness
            star.brightness = star.brightness * 0.995 + (p.random(0.3, 1) * 0.005)
          }
        }
      } else if (this.currentSim === 1) {
        // Electrical - particle attraction for electrical simulation
        for (let particle of particles) {
          let distance = p.dist(p.mouseX, p.mouseY, particle.x, particle.y)
          if (distance < 150) {
            let force = p.map(distance, 0, 150, 0.5, 0)
            let angle = p.atan2(particle.y - p.mouseY, particle.x - p.mouseX)
            if (particle.vx !== undefined) {
              particle.vx += p.cos(angle) * force
              particle.vy += p.sin(angle) * force
            }
          }
        }
      } else if (this.currentSim === 2) {
        // Flocking - create avoidance behavior
        for (let boid of particles) {
          let distance = p.dist(p.mouseX, p.mouseY, boid.x, boid.y)
          if (distance < 100) {
            let force = p.map(distance, 0, 100, 2, 0)
            let angle = p.atan2(boid.y - p.mouseY, boid.x - p.mouseX)
            boid.vx += p.cos(angle) * force * 0.1
            boid.vy += p.sin(angle) * force * 0.1
          }
        }
      } else if (this.currentSim === 3) {
        // Cloth Fabric - slice through fabric with mouse (living cloth)
        if (Array.isArray(particles) && Array.isArray(particles[0])) {
          let clothWidth = particles.length
          let clothHeight = particles[0].length
          let cutRadius = 25
          let currentFrame = p.frameCount
          
          // Find connections that cross the mouse path and cut them
          for (let x = 0; x < clothWidth; x++) {
            for (let y = 0; y < clothHeight; y++) {
              let particle = particles[x][y]
              
              // Check if mouse is near this particle's connections
              let mouseDistance = p.dist(p.mouseX, p.mouseY, particle.x, particle.y)
              
              if (mouseDistance < cutRadius) {
                // Cut connections around this particle
                if (particle.connections.right && particle.connections.right.active && x < clothWidth - 1) {
                  let neighbor = particles[x + 1][y]
                  let neighborDistance = p.dist(p.mouseX, p.mouseY, neighbor.x, neighbor.y)
                  if (neighborDistance < cutRadius) {
                    particle.connections.right.active = false
                    particle.connections.right.cutTime = currentFrame
                  }
                }
                
                if (particle.connections.down && particle.connections.down.active && y < clothHeight - 1) {
                  let neighbor = particles[x][y + 1]
                  let neighborDistance = p.dist(p.mouseX, p.mouseY, neighbor.x, neighbor.y)
                  if (neighborDistance < cutRadius) {
                    particle.connections.down.active = false
                    particle.connections.down.cutTime = currentFrame
                  }
                }
                
                if (particle.connections.diagonal1 && particle.connections.diagonal1.active && x < clothWidth - 1 && y < clothHeight - 1) {
                  let neighbor = particles[x + 1][y + 1]
                  let neighborDistance = p.dist(p.mouseX, p.mouseY, neighbor.x, neighbor.y)
                  if (neighborDistance < cutRadius) {
                    particle.connections.diagonal1.active = false
                    particle.connections.diagonal1.cutTime = currentFrame
                  }
                }
                
                if (particle.connections.diagonal2 && particle.connections.diagonal2.active && x > 0 && y < clothHeight - 1) {
                  let neighbor = particles[x - 1][y + 1]
                  let neighborDistance = p.dist(p.mouseX, p.mouseY, neighbor.x, neighbor.y)
                  if (neighborDistance < cutRadius) {
                    particle.connections.diagonal2.active = false
                    particle.connections.diagonal2.cutTime = currentFrame
                  }
                }
              }
            }
          }
        }
      } else if (this.currentSim === 4) {
        // Particle Swarm - mouse attracts particles
        for (let particle of particles) {
          let dx = p.mouseX - particle.x
          let dy = p.mouseY - particle.y
          let distance = p.sqrt(dx * dx + dy * dy)
          
          if (distance < 120) {
            let force = p.map(distance, 0, 120, 0.3, 0)
            let angle = p.atan2(dy, dx)
            particle.vx += p.cos(angle) * force
            particle.vy += p.sin(angle) * force
          }
        }
      }
    },
    
    googleAuth() {
      this.authenticating = true
      let provider = new GoogleAuthProvider()
      let auth = getAuth()
      provider.addScope('email')
      provider.addScope('profile')
      provider.addScope('https://www.googleapis.com/auth/userinfo.email')
      provider.addScope('https://www.googleapis.com/auth/userinfo.profile')
      // if local use signInWithPopup, otherwise use signInWithRedirect
      let loginMethod = location.hostname === 'localhost' ? signInWithPopup(auth, provider) : signInWithRedirect(auth, provider)
      loginMethod.then( credentials => {
        // Don't have to redirect as App will handle that
      }).catch( error => {
        if (error.code == 'auth/popup-closed-by-user') {
          console.log('Google authentication popup closed.')
        } else {
          console.log(error.message)
        }
        this.authenticating = false
      })
    },
    
    drawStarFieldSim(p, particles, gridLines, time) {
      // Rotating star field simulation
      let centerX = p.width / 2
      let centerY = p.height / 2
      let rotationSpeed = 0.001 // Very slow rotation
      let shootingStarManager = gridLines[0]
      
      // Update and draw stars with rotation and flickering
      for (let star of particles) {
        // Rotate star position around center
        star.angle += rotationSpeed
        star.x = centerX + p.cos(star.angle) * star.radius
        star.y = centerY + p.sin(star.angle) * star.radius
        
        // Calculate flickering effect
        let flicker = p.sin(time * star.flickerSpeed + star.flickerOffset) * 0.3 + 0.7
        let currentBrightness = star.brightness * flicker
        
        // Draw star with glow effect
        let alpha = currentBrightness * 255
        
        // Outer glow
        for (let r = star.size + 4; r > 0; r -= 1) {
          let glowAlpha = p.map(r, 0, star.size + 4, alpha, alpha * 0.1)
          p.fill(200, 220, 255, glowAlpha)
          p.noStroke()
          p.circle(star.x, star.y, r)
        }
        
        // Core star
        p.fill(255, 255, 255, alpha)
        p.noStroke()
        p.circle(star.x, star.y, star.size)
        
        // Add subtle sparkle effect for larger stars
        if (star.size > 2.5 && currentBrightness > 0.8) {
          p.stroke(255, 255, 255, alpha * 0.8)
          p.strokeWeight(0.5)
          let sparkleSize = star.size * 1.5
          p.line(star.x - sparkleSize, star.y, star.x + sparkleSize, star.y)
          p.line(star.x, star.y - sparkleSize, star.x, star.y + sparkleSize)
        }
      }
      
      // Generate shooting stars occasionally
      if (p.frameCount - shootingStarManager.lastShootingStar > p.random(300, 600)) {
        shootingStarManager.shootingStars.push({
          x: p.random(-100, p.width + 100),
          y: p.random(-100, p.height/3),
          vx: p.random(3, 8),
          vy: p.random(1, 4),
          life: 1.0,
          maxLife: 1.0,
          trail: []
        })
        shootingStarManager.lastShootingStar = p.frameCount
      }
      
      // Update and draw shooting stars
      for (let i = shootingStarManager.shootingStars.length - 1; i >= 0; i--) {
        let shootingStar = shootingStarManager.shootingStars[i]
        
        // Update position
        shootingStar.x += shootingStar.vx
        shootingStar.y += shootingStar.vy
        shootingStar.life -= 0.015
        
        // Add to trail
        shootingStar.trail.push({x: shootingStar.x, y: shootingStar.y})
        if (shootingStar.trail.length > 15) {
          shootingStar.trail.shift()
        }
        
        // Remove if dead or off screen
        if (shootingStar.life <= 0 || shootingStar.x > p.width + 100 || shootingStar.y > p.height + 100) {
          shootingStarManager.shootingStars.splice(i, 1)
          continue
        }
        
        // Draw shooting star trail
        for (let j = 0; j < shootingStar.trail.length; j++) {
          let trailPoint = shootingStar.trail[j]
          let trailAlpha = p.map(j, 0, shootingStar.trail.length, 20, 200) * shootingStar.life
          let trailSize = p.map(j, 0, shootingStar.trail.length, 1, 4)
          
          p.fill(255, 255, 200, trailAlpha)
          p.noStroke()
          p.circle(trailPoint.x, trailPoint.y, trailSize)
        }
        
        // Draw shooting star head
        let headAlpha = 255 * shootingStar.life
        p.fill(255, 255, 150, headAlpha)
        p.noStroke()
        p.circle(shootingStar.x, shootingStar.y, 4)
        
        // Add bright glow to head
        p.fill(255, 255, 100, headAlpha * 0.5)
        p.circle(shootingStar.x, shootingStar.y, 8)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.landing-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.canvas-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.content-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none; // Allow mouse interaction with canvas
  
  // Re-enable pointer events for interactive elements
  .icon, .key, .login-btn {
    pointer-events: auto;
  }
}

.icon {
  padding: 20px;
  border-radius: 44px;
  // border: 4px solid rgba(0, 0, 0, 0.8);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
}

.main-title {
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  font-weight: 300;
  font-size: 2.5rem;
  margin-bottom: 16px;
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  font-size: 1.1rem;
}

.login-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  color: white !important;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
  
  &:before {
    background-color: transparent !important;
  }
}

.key {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
}
</style>